import { useCallback, useEffect, useState } from "react";
import { TranslationService } from "../../../services/TranslationService";
import { ToastService } from "../../shared/bootstrap/Toast";
import { ModalService, ModalParams } from "../../shared/bootstrap/modal/Modal";
import PaymentGatewayTransactionLogDetail from "./PaymentGatewayTransactionLogDetail";
import PaymentGatewayTransactionService from "../../../services/PaymentGatewayTransactionService";
import { PaymentGatewayTransactionListResponse } from "../../../entities/company/PaymentProvider/PaymentGatewayTransactionListResponse";
import Table, { TableHeader } from "../../shared/Table";
import TableContext, { TableContextValues } from "../../task/TableContext";
import ErrorMessage from "../../shared/components/Error";
import Loading from "../../shared/components/Loading";

const PAGE_SIZE = 20;

const tableHeaders: TableHeader[] = [
  new TableHeader(
    "PaymentLinkTransactionId",
    "ID de Transacción",
    false,
    false
  ),
  new TableHeader("Created", "Fecha y hora", false, false),
  new TableHeader("Amount", "Monto", false, false),
  new TableHeader("IsSuccessful", "Exitoso", false, false),
  new TableHeader("NumberInvoicesProcess", "Facturas", false, false)
];

const PaymentGatewayTransactionLog = () => {
  const { translate } = TranslationService;

  const [response, setResponse] =
    useState<PaymentGatewayTransactionListResponse>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, _setCurrentPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(1);
  const [pageSize] = useState<number>(PAGE_SIZE);

  const requestData = async () => {
    setLoading(true);
    const result = await PaymentGatewayTransactionService.getList(
      currentPage,
      pageSize
    );
    if (result instanceof Error) {
      setError(true);
      setLoading(false);
      return;
    }

    // Calcular total de páginas basado en el tamaño de página
    // Nota: Si el backend no devuelve paginación, asumimos que recibimos todos los resultados
    const totalPages = Math.max(1, Math.ceil(result.list.length / pageSize));
    setResponse(result);
    setPageCount(totalPages);
    setLoading(false);
    setError(false);
  };

  const requestDataCallback = useCallback(requestData, [currentPage, pageSize]);

  useEffect(() => {
    requestDataCallback();
  }, [currentPage, requestDataCallback]);

  const setCurrentPage = (page: number) => {
    if (page >= 0 && page <= pageCount - 1) {
      _setCurrentPage(page);
    }
  };

  const handleRefresh = async () => {
    try {
      await requestData();
      ToastService.showToast(
        "Historial actualizado correctamente",
        undefined,
        "success"
      );
    } catch (e: any) {
      ToastService.showToast(
        e?.message ?? "Error al actualizar el historial",
        undefined,
        "danger"
      );
    }
  };

  const openDetailModal = (
    transaction: PaymentGatewayTransactionListResponse["list"][0]
  ) => {
    const p = new ModalParams();
    p.size = "modal-xl";
    p.title = "Detalle de Transacción";
    p.closeClickingOutside = true;
    p.children = (
      <PaymentGatewayTransactionLogDetail
        transaction={transaction}
        onClose={() => ModalService.hideModal()}
      />
    );
    ModalService.showModal(p);
  };

  const tableContext =
    new TableContextValues<PaymentGatewayTransactionListResponse>();
  tableContext.loading = loading;
  tableContext.error = error;
  tableContext.response = response;
  tableContext.currentPage = currentPage;
  tableContext.setCurrentPage = setCurrentPage;
  tableContext.pageCount = pageCount;
  tableContext.reload = requestData;
  tableContext.pageSize = pageSize;

  const PaymentGatewayTransactionItem = ({
    data
  }: {
    data: PaymentGatewayTransactionListResponse["list"][0];
  }) => (
    <tr
      key={data.PaymentLinkTransactionId}
      style={{ cursor: "pointer" }}
      onClick={() => openDetailModal(data)}
    >
      <td>{data.PaymentLinkTransactionId}</td>
      <td>{data.Created ? new Date(data.Created).toLocaleString() : ""}</td>
      <td>
        {data.Amount?.toLocaleString("es-MX", {
          style: "currency",
          currency: "MXN"
        }) || data.Amount}
      </td>
      <td>
        {data.IsSuccessful ? (
          <span className="badge badge-success">Sí</span>
        ) : (
          <span className="badge badge-danger">No</span>
        )}
      </td>
      <td>{data.NumberInvoicesProcess}</td>
    </tr>
  );

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <ErrorMessage
        onRefresh={requestDataCallback}
        message={"Error al cargar el historial de transacciones"}
      />
    );
  }

  return (
    <div className="main-layout-container mx-3">
      <div className="main-panel">
        <h4 className="mb-2">Historial de Transacciones</h4>
        <div className="card">
          <div className="d-flex align-items-center justify-content-end mb-2 gap-2">
            <button className="btn btn-primary" onClick={handleRefresh}>
              <i className="fa fa-sync mr-1"></i>
              {translate.Refresh}
            </button>
          </div>
          <TableContext.Provider value={tableContext}>
            <Table
              headers={tableHeaders}
              item={PaymentGatewayTransactionItem}
            />
          </TableContext.Provider>
        </div>
      </div>
    </div>
  );
};

export default PaymentGatewayTransactionLog;
